import { PayloadAction } from "@reduxjs/toolkit";
import { AirFilterType, SeaFilterType } from "features/shared/filter.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { useSeaQueryKey } from "features/shared/useSeaQueryKey";
import { AirSavedCardModel, CountByAirStatusModel } from "entities/AirSavedCard";
import { CountBySeaStatusModel, isSeaSavedShipmentModel, useSeaDeleteCardMutation } from "entities/SeaSavedCard";
import { SeaSavedCardModel, useSeaInfiniteScrollFetch } from "entities/SeaSavedCard";
import { SeaSearchCardModel } from "entities/SeaSearchCard";
import { useAppDispatch } from "shared/lib/redux";

type UseSeaDeleteAllCardsActionParams = {
  filter: SeaFilterType | AirFilterType;
  isSeaFilter?: (filter: SeaFilterType | AirFilterType) => filter is SeaFilterType;
  filteredCards: (SeaSavedCardModel | AirSavedCardModel)[];
  seaSavedCards: SeaSavedCardModel[];
  seaSearchCards: SeaSearchCardModel[];
  cardsByTags: (SeaSavedCardModel | AirSavedCardModel)[];
  closeDeleteAllCardsModal: () => void;
  setAllStatuses: (statuses: CountBySeaStatusModel[] | CountByAirStatusModel[]) => void;
  actions: {
    appendFilteredCardsAction: (payload: {
      data: (SeaSavedCardModel | AirSavedCardModel)[];
      unique?: boolean;
    }) => PayloadAction<{ data: (SeaSavedCardModel | AirSavedCardModel)[]; unique?: boolean }>;
    appendSavedCardsAction: (payload: {
      data: SeaSavedCardModel[];
      unique?: boolean;
    }) => PayloadAction<{ data: SeaSavedCardModel[]; unique?: boolean }>;
    removeFilteredCardAction: (
      cards: SeaSavedCardModel | AirSavedCardModel
    ) => PayloadAction<SeaSavedCardModel | AirSavedCardModel>;
    removeCardByTagAction: (
      cards: SeaSavedCardModel | AirSavedCardModel
    ) => PayloadAction<SeaSavedCardModel | AirSavedCardModel>;
    removeSavedCardAction: (cards: SeaSavedCardModel) => PayloadAction<SeaSavedCardModel>;
    removeSearchCardAction: (cards: SeaSearchCardModel) => PayloadAction<SeaSearchCardModel>;
    refreshTags: () => void;
  };
};

type UseSeaDeleteAllCardsActionReturnType = {
  deleteAllSeaCards: () => void;
};

export const useSeaDeleteAllCardsAction = ({
  filteredCards,
  seaSavedCards,
  seaSearchCards,
  closeDeleteAllCardsModal,
  actions,
  isSeaFilter,
  filter,
  setAllStatuses,
  cardsByTags,
}: UseSeaDeleteAllCardsActionParams): UseSeaDeleteAllCardsActionReturnType => {
  const dispatch = useAppDispatch();
  const {
    appendFilteredCardsAction,
    appendSavedCardsAction,
    removeFilteredCardAction,
    removeCardByTagAction,
    removeSavedCardAction,
    removeSearchCardAction,
    refreshTags,
  } = actions;
  const { isCurrentViewFilteredCards, isCurrentViewMain, showMainView, isCurrentViewCardsByTag } = usePanelViews();
  const { deleteCard } = useSeaDeleteCardMutation();
  const { queryKey } = useSeaQueryKey({
    isCurrentViewFilteredCards,
    isCurrentViewMain,
  });

  const { resetSeaCardsQuery, setRequestFilterSync } = useSeaInfiniteScrollFetch({
    onBeforeRequest: () => {
      if (isSeaFilter?.(filter)) setRequestFilterSync({ where: filter.where, order: filter.order });
    },
    queryKey: [queryKey],
    shouldRequestOnMount: false,
    onSuccess: ({ page, countByStatus }) => {
      if (!countByStatus.length) {
        setAllStatuses([]);
      }

      if (!page.length) {
        showMainView();
        return;
      }
      if (isCurrentViewFilteredCards) {
        dispatch(appendFilteredCardsAction({ data: page }));
      }
      if (isCurrentViewMain) {
        dispatch(appendSavedCardsAction({ data: page }));
      }
    },
  });

  const deleteSearchCards = (cards: SeaSearchCardModel[]) => {
    cards.forEach((card) => {
      dispatch(removeSearchCardAction(card));
    });
  };

  const deleteCards = async (cards: SeaSavedCardModel[]) => {
    const promises = cards.map((card) => {
      return deleteCard(card.id);
    });

    await Promise.all(promises)
      .then(() => {
        cards.forEach((card) => {
          dispatch(removeFilteredCardAction(card));
          dispatch(removeSavedCardAction(card));
          dispatch(removeCardByTagAction(card));
        });
      })
      .finally(() => {
        resetSeaCardsQuery();
        closeDeleteAllCardsModal();
        refreshTags();
      });
  };

  const deleteAllSeaCards = async () => {
    try {
      if (isCurrentViewFilteredCards) {
        await deleteCards(filteredCards.filter((card) => isSeaSavedShipmentModel(card)));
      }
      if (isCurrentViewMain) {
        await deleteCards(seaSavedCards);
        deleteSearchCards(seaSearchCards);
      }
      if (isCurrentViewCardsByTag) {
        await deleteCards(cardsByTags.filter((card) => isSeaSavedShipmentModel(card)));
      }
    } catch (_) {
      closeDeleteAllCardsModal();
    }
  };

  return { deleteAllSeaCards };
};
